import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateConfig } from "../../../hooks/useSettings";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { Icon } from "../../ui/UIElements/Icon";

// Fields that should only accept numeric input
const NUMERIC_FIELDS = ["max_distance_pickup", "max_days_preorder"];

// Fields with character limits
const CHAR_LIMIT_FIELDS = {
  format_number_currency: 3,
};

const TECHNICAL_ENUM_PATTERN = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/;
const TECHNICAL_PATH_PATTERN = /^[A-Za-z0-9]+(?:[._][A-Za-z0-9]+)+$/;
const SHORT_ACRONYM_PATTERN = /^[A-Z]{2,3}$/;

const isTechnicalLabel = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();

  if (!trimmed || trimmed.includes(" ")) return false;

  return (
    TECHNICAL_ENUM_PATTERN.test(trimmed) || TECHNICAL_PATH_PATTERN.test(trimmed)
  );
};

const humanizeTechnicalLabel = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  const keySegment = trimmed.split(".").pop() || trimmed;

  if (SHORT_ACRONYM_PATTERN.test(keySegment)) {
    return keySegment;
  }

  const words = keySegment
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean);

  if (words.length === 0) {
    return keySegment;
  }

  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const SettingsConfig = ({ config }) => {
  const { t, i18n } = useTranslation(["settings", "common"]);
  const [value, setValue] = useState(() => {
    if (config.value === null || config.value === undefined) return "";
    if (typeof config.value === "string") return config.value;
    return String(config.value);
  });
  const [isEditing, setIsEditing] = useState(false);
  const updateConfigMutation = useUpdateConfig();
  const toast = useToast();

  // Sync local value with config prop when it changes
  useEffect(() => {
    if (config.value === null || config.value === undefined) {
      setValue("");
    } else if (typeof config.value === "string") {
      setValue(config.value);
    } else {
      setValue(String(config.value));
    }
  }, [config.value]);

  const handleSave = async () => {
    // Validate multi-select configs require at least one selection
    if (config.type === 3) {
      const selectedValues = value
        ? value.split("|").filter((v) => v !== "")
        : [];
      if (selectedValues.length === 0) {
        toast.error(t("config.toasts.atLeastOneRequired"));
        return;
      }
    }

    try {
      await updateConfigMutation.mutateAsync({
        configId: config.id,
        key: config.key,
        value: value,
      });
      handleApiSuccess(t("config.toasts.updated"), toast, {
        title: t("states.success", { ns: "common" }),
      });
      setIsEditing(false);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("config.toasts.updateFailedTitle"),
        defaultMessage: t("config.toasts.updateFailedMessage"),
      });
    }
  };

  const handleCancel = () => {
    setValue(config.value || "");
    setIsEditing(false);
  };

  const resolveOptionLabel = (option) => {
    const textValue =
      typeof option?.text === "string" ? option.text.trim() : "";
    const optionValue =
      typeof option?.value === "string" ? option.value.trim() : "";

    const translationCandidates = [...new Set([optionValue, textValue])]
      .filter((candidate) => candidate && isTechnicalLabel(candidate))
      .flatMap((candidate) => [candidate, candidate.toLowerCase()]);

    for (const candidate of translationCandidates) {
      const scopedKey = `config.optionLabels.${config.key}.${candidate}`;
      if (i18n.exists(scopedKey, { ns: "settings" })) {
        return t(scopedKey, { ns: "settings" });
      }

      const commonKey = `config.optionLabels.common.${candidate}`;
      if (i18n.exists(commonKey, { ns: "settings" })) {
        return t(commonKey, { ns: "settings" });
      }
    }

    if (textValue) {
      return isTechnicalLabel(textValue)
        ? humanizeTechnicalLabel(textValue)
        : textValue;
    }

    if (optionValue) {
      return isTechnicalLabel(optionValue)
        ? humanizeTechnicalLabel(optionValue)
        : optionValue;
    }

    return t("config.notSet");
  };

  const getDisplayText = () => {
    if (config.type === 2 && config.options) {
      const selectedOption = config.options.find(
        (option) => option.value === value,
      );
      if (selectedOption) {
        return resolveOptionLabel(selectedOption);
      }

      if (!value) {
        return t("config.notSet");
      }

      return isTechnicalLabel(value) ? humanizeTechnicalLabel(value) : value;
    }

    if (config.type === 3 && config.options) {
      if (!value) return t("config.notSet");
      const selectedValues = value.split("|").filter((v) => v !== "");
      if (selectedValues.length === 0) return t("config.notSet");

      const selectedTexts = selectedValues
        .map((val) => {
          const option = config.options.find((opt) => opt.value === val);
          if (option) {
            return resolveOptionLabel(option);
          }

          return isTechnicalLabel(val) ? humanizeTechnicalLabel(val) : val;
        })
        .filter(Boolean);

      return selectedTexts.length > 0
        ? selectedTexts.join(", ")
        : t("config.notSet");
    }

    return value || t("config.notSet");
  };

  const renderInput = () => {
    if (!isEditing) {
      return (
        <div className="flex w-full items-center justify-between">
          <span className="text-sm text-gray-300">
            {updateConfigMutation.isPending ? (
              <div className="flex items-center space-x-2">
                <Icon
                  name="Loader"
                  className="h-4 w-4 animate-spin text-blue-400"
                />
                <span>{t("config.actions.updating")}</span>
              </div>
            ) : (
              getDisplayText()
            )}
          </span>
          <button
            onClick={() => setIsEditing(true)}
            disabled={updateConfigMutation.isPending}
            className="p-1 text-gray-400 transition-colors hover:text-white disabled:cursor-not-allowed disabled:text-gray-600"
            aria-label={t("config.actions.editAria")}
          >
            <Icon name="Edit" className="h-4 w-4" />
          </button>
        </div>
      );
    }

    if (config.type === 2 && config.options) {
      // Select dropdown
      return (
        <div className="flex w-full items-center space-x-2">
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={updateConfigMutation.isPending}
            className="custom-select flex-1 rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {config.options.map((option) => (
              <option key={option.value} value={option.value}>
                {resolveOptionLabel(option)}
              </option>
            ))}
          </select>
          <button
            onClick={handleSave}
            disabled={updateConfigMutation.isPending}
            className="rounded-md bg-green-600 p-2 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-600"
            aria-label={t("actions.save", { ns: "common" })}
          >
            {updateConfigMutation.isPending ? (
              <Icon name="Loader" className="h-4 w-4 animate-spin" />
            ) : (
              <Icon name="Check" className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={handleCancel}
            disabled={updateConfigMutation.isPending}
            className="rounded-md bg-gray-600 p-2 text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-500"
            aria-label={t("actions.cancel", { ns: "common" })}
          >
            <Icon name="X" className="h-4 w-4" />
          </button>
        </div>
      );
    } else if (config.type === 3 && config.options) {
      // Multi-select (checkboxes)
      const selectedValues = value
        ? value.split("|").filter((v) => v !== "")
        : [];

      const handleMultiSelectChange = (optionValue) => {
        const isSelected = selectedValues.includes(optionValue);
        let newValues;
        if (isSelected) {
          newValues = selectedValues.filter((v) => v !== optionValue);
        } else {
          newValues = [...selectedValues, optionValue];
        }
        setValue(newValues.join("|"));
      };

      return (
        <div className="w-full">
          <div className="mt-2 mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {config.options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <div
                  key={option.value}
                  onClick={() =>
                    !updateConfigMutation.isPending &&
                    handleMultiSelectChange(option.value)
                  }
                  className={`relative flex cursor-pointer items-center justify-between rounded-lg border-2 p-4 transition-all duration-200 ${
                    isSelected
                      ? "border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20"
                      : "border-gray-600 bg-gray-800/50 hover:border-gray-500 hover:bg-gray-800"
                  } ${
                    updateConfigMutation.isPending
                      ? "cursor-not-allowed opacity-50"
                      : ""
                  } `}
                >
                  {/* Option text */}
                  <div className="pr-8">
                    <span
                      className={`text-sm font-medium ${
                        isSelected ? "text-blue-300" : "text-gray-300"
                      }`}
                    >
                      {resolveOptionLabel(option)}
                    </span>
                  </div>
                  {/* Check icon */}
                  <div className="">
                    {isSelected ? (
                      <div className="rounded-full bg-blue-500 p-1">
                        <Icon name="Check" className="h-3 w-3 text-white" />
                      </div>
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-500"></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected count indicator */}
          {selectedValues.length > 0 && (
            <div className="mb-3 text-xs font-medium text-blue-400">
              {selectedValues.length}{" "}
              {selectedValues.length === 1
                ? t("config.selection.item")
                : t("config.selection.items")}{" "}
              {t("config.selection.selected")}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={handleSave}
              disabled={updateConfigMutation.isPending}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-green-600/20 transition-colors hover:bg-green-700 hover:shadow-green-600/30 disabled:cursor-not-allowed disabled:bg-gray-600"
            >
              {updateConfigMutation.isPending ? (
                <div className="flex items-center space-x-2">
                  <Icon name="Loader" className="h-4 w-4 animate-spin" />
                  <span>{t("config.actions.saving")}</span>
                </div>
              ) : (
                <span className="flex items-center space-x-2">
                  <Icon name="Check" className="h-4 w-4" />
                  <span>{t("actions.save", { ns: "common" })}</span>
                </span>
              )}
            </button>
            <button
              onClick={handleCancel}
              disabled={updateConfigMutation.isPending}
              className="rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-500"
            >
              {t("actions.cancel", { ns: "common" })}
            </button>
          </div>
        </div>
      );
    } else {
      // Text input
      const isNumericField = NUMERIC_FIELDS.includes(config.key);
      const charLimit = CHAR_LIMIT_FIELDS[config.key] || null;

      return (
        <div className="flex w-full items-center space-x-2">
          <input
            type={isNumericField ? "number" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={updateConfigMutation.isPending}
            className="flex-1 rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={t("config.placeholders.enterValue")}
            min={isNumericField ? "0" : null}
            step={isNumericField ? "1" : null}
            maxLength={charLimit}
          />
          <button
            onClick={handleSave}
            disabled={updateConfigMutation.isPending}
            className="rounded-md bg-green-600 p-2 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-600"
            aria-label={t("actions.save", { ns: "common" })}
          >
            {updateConfigMutation.isPending ? (
              <Icon name="Loader" className="h-4 w-4 animate-spin" />
            ) : (
              <Icon name="Check" className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={handleCancel}
            disabled={updateConfigMutation.isPending}
            className="rounded-md bg-gray-600 p-2 text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-500"
            aria-label={t("actions.cancel", { ns: "common" })}
          >
            <Icon name="X" className="h-4 w-4" />
          </button>
        </div>
      );
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-1 flex items-center space-x-2">
            <h4 className="text-sm font-medium text-white">{config.name}</h4>
          </div>
          {config.description && (
            <p className="mb-1 text-xs text-gray-400">{config.description}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">{renderInput()}</div>
    </div>
  );
};

export default SettingsConfig;
